import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CaretLeft, CaretRight, CheckCircle, Airplane, FloppyDisk, Clock, ArrowLeft,
  Warning, Sparkle,
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function isoDate(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }
function todayIso() {
  const t = new Date();
  return isoDate(t.getFullYear(), t.getMonth(), t.getDate());
}

function buildMonthGrid(year, month) {
  // month is 0-indexed
  const firstDay = new Date(year, month, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function WorkerAvailability() {
  const navigate = useNavigate();
  const { t } = useWorkerLang();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [vacationMode, setVacationMode] = useState(false);
  const [vacationUntil, setVacationUntil] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return { year: t.getFullYear(), month: t.getMonth() };
  });

  const today = todayIso();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/worker-availability/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const a = res.data?.availability || {};
        setBlockedDates(new Set(a.blocked_dates || []));
        setVacationMode(!!a.vacation_mode);
        setVacationUntil(a.vacation_until || '');
        setNote(a.note || '');
        setStatus(res.data?.status || null);
      } catch (err) {
        if (err.response?.status === 403) {
          toast({ title: 'Access Denied', description: 'Only workers/contractors can manage availability.', variant: 'destructive' });
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  const toggleDate = (day) => {
    if (!day) return;
    const iso = isoDate(cursor.year, cursor.month, day);
    if (iso < today) return; // cannot block past dates
    setBlockedDates(prev => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  };

  const prevMonth = () => setCursor(({ year, month }) => {
    const d = new Date(year, month - 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const nextMonth = () => setCursor(({ year, month }) => {
    const d = new Date(year, month + 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/worker-availability/me`, {
        blocked_dates: Array.from(blockedDates),
        vacation_mode: vacationMode,
        vacation_until: vacationMode ? vacationUntil : null,
        note,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data?.status || null);
      toast({ title: 'Availability saved', description: 'Contractors will now see your updated schedule.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Could not save availability', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const quickBlock = (days) => {
    const start = new Date();
    const next = new Set(blockedDates);
    for (let i = 0; i < days; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      next.add(isoDate(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    setBlockedDates(next);
  };

  const clearAll = () => setBlockedDates(new Set());

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <Clock size={32} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="worker-availability-page">
      {/* Header */}
      <div className="bg-neutral-900 border-b-4 border-emerald-500">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white font-bold"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} weight="bold" /> Back
          </button>
          <h1 className="text-lg sm:text-2xl font-black uppercase text-white flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-emerald-400" />
            {t('my_availability_title')}
          </h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#10B981]">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 rounded-lg bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200" data-testid="prev-month-btn">
              <CaretLeft size={18} weight="bold" />
            </button>
            <h2 className="font-black text-lg text-neutral-900 dark:text-white" data-testid="month-label">
              {MONTH_NAMES[cursor.month]} {cursor.year}
            </h2>
            <button onClick={nextMonth} className="p-2 rounded-lg bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200" data-testid="next-month-btn">
              <CaretRight size={18} weight="bold" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1 text-center">
            {WEEK_DAYS.map((w) => (
              <div key={w} className="text-[11px] font-bold text-neutral-500 uppercase py-1">{w}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) return <div key={`empty-${cursor.year}-${cursor.month}-${idx}`} />;
              const iso = isoDate(cursor.year, cursor.month, day);
              const isPast = iso < today;
              const isToday = iso === today;
              const isBlocked = blockedDates.has(iso);
              const isVacation = vacationMode && vacationUntil && iso <= vacationUntil && iso >= today;
              let cls = 'aspect-square flex items-center justify-center text-sm font-bold rounded-md border-2 transition-all';
              if (isPast) cls += ' bg-neutral-100 dark:bg-neutral-900 text-neutral-400 border-neutral-200 dark:border-neutral-800 cursor-not-allowed';
              else if (isVacation) cls += ' bg-amber-500 text-white border-amber-600 cursor-not-allowed';
              else if (isBlocked) cls += ' bg-red-500 text-white border-red-600 hover:bg-red-600 cursor-pointer';
              else cls += ' bg-white dark:bg-neutral-700 text-neutral-800 dark:text-neutral-100 border-neutral-300 dark:border-neutral-600 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 cursor-pointer';
              if (isToday) cls += ' ring-2 ring-[#2B4C6F]';
              return (
                <button
                  key={iso}
                  onClick={() => toggleDate(day)}
                  disabled={isPast || isVacation}
                  className={cls}
                  data-testid={`day-${iso}`}
                  title={isBlocked ? 'Blocked' : isVacation ? 'Vacation' : 'Available (click to block)'}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-5 text-[11px] text-neutral-600 dark:text-neutral-400">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border-2 border-neutral-300 rounded" /> {t('available_legend')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 border-2 border-red-600 rounded" /> {t('blocked_legend')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 border-2 border-amber-600 rounded" /> {t('vacation_legend')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border-2 ring-2 ring-[#2B4C6F] rounded" /> {t('today_legend')}</span>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button onClick={() => quickBlock(3)} className="text-xs font-bold px-3 py-1.5 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 rounded-lg" data-testid="block-3-days-btn">{t('block_3_days')}</button>
            <button onClick={() => quickBlock(7)} className="text-xs font-bold px-3 py-1.5 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 rounded-lg" data-testid="block-7-days-btn">{t('block_7_days')}</button>
            <button onClick={clearAll} className="text-xs font-bold px-3 py-1.5 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 rounded-lg text-red-600" data-testid="clear-blocked-btn">{t('clear_all_blocked')}</button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status preview */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5">
            <h3 className="text-xs font-black uppercase text-neutral-500 mb-3">Your Status</h3>
            {status?.available_today ? (
              <div className="flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-2 rounded-lg font-bold text-sm">
                <CheckCircle size={18} weight="fill" /> Available today
              </div>
            ) : status?.vacation_mode ? (
              <div className="flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-2 rounded-lg font-bold text-sm">
                <Airplane size={18} weight="fill" /> On vacation
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-neutral-100 text-neutral-700 px-3 py-2 rounded-lg font-bold text-sm">
                <Clock size={18} weight="fill" /> Booked today
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-2">
              {status?.blocked_next_30_days || 0} {t('days_blocked_30')}
            </p>
          </div>

          {/* Vacation Mode */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5">
            <h3 className="text-xs font-black uppercase text-neutral-500 mb-3">{t('vacation_mode')}</h3>
            <button
              type="button"
              onClick={() => setVacationMode(!vacationMode)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${vacationMode ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-neutral-200 dark:border-neutral-600 hover:border-neutral-400'}`}
              data-testid="vacation-toggle"
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${vacationMode ? 'bg-amber-500 border-amber-500' : 'border-neutral-400'}`}>
                {vacationMode && <CheckCircle size={14} weight="bold" className="text-white" />}
              </div>
              <Airplane size={18} weight="bold" className={vacationMode ? 'text-amber-600' : 'text-neutral-400'} />
              <span className="font-bold text-sm text-neutral-900 dark:text-white">{t('going_on_vacation')}</span>
            </button>
            {vacationMode && (
              <div className="mt-3">
                <label className="block text-[11px] font-bold uppercase text-neutral-500 mb-1">{t('vacation_until')}</label>
                <input
                  type="date"
                  value={vacationUntil}
                  min={today}
                  onChange={(e) => setVacationUntil(e.target.value)}
                  className="w-full h-10 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  data-testid="vacation-until-input"
                />
              </div>
            )}
          </div>

          {/* Note */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5">
            <label className="block text-xs font-black uppercase text-neutral-500 mb-2">{t('private_note')}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              rows={2}
              placeholder={t('note_placeholder')}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white resize-none"
              data-testid="note-input"
            />
            <p className="text-[10px] text-neutral-400 mt-1">{note.length}/200 — {t('not_visible_contractors')}</p>
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving || (vacationMode && !vacationUntil)}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="save-availability-btn"
          >
            {saving ? <Clock size={18} weight="bold" className="animate-spin" /> : <FloppyDisk size={18} weight="bold" />}
            {saving ? t('saving') : t('save_availability')}
          </button>
          {vacationMode && !vacationUntil && (
            <p className="text-[11px] text-red-500 flex items-center gap-1"><Warning size={12} weight="bold" /> Pick a return date</p>
          )}
        </div>
      </div>
    </div>
  );
}
